import React from 'react';
import { AlertTriangle, Loader2, Lock, RefreshCw, ShieldCheck } from 'lucide-react';
import { UnlockButton } from './UnlockButton';
import { reportUnlockFeatures } from '../../../data/reportUnlockFeatures';
import { PLAN_PRICES_USD, formatUsd } from '../../../data/pricingPlans';
import type { CheckoutState } from '../../../hooks/useReportUnlock';
export type PaymentReturnNotice = 'cancelled' | 'failed' | 'expired';
const NOTICE_COPY: Record<PaymentReturnNotice, string> = {
  cancelled: 'Checkout was cancelled, so this report is still locked. You can unlock it whenever you’re ready.',
  failed: 'The payment didn’t go through, so this report is still locked. You can try again below.',
  expired: 'The checkout session expired before payment, so this report is still locked. Start a new checkout below.'
};
interface ReportUnlockCardProps {
  patternCount: number;
  state: CheckoutState;
  error: string | null;
  notice: PaymentReturnNotice | null;
  onUnlock: () => void;
  onCheckAgain: () => void;
}
export function ReportUnlockCard({
  patternCount,
  state,
  error,
  notice,
  onUnlock,
  onCheckAgain
}: ReportUnlockCardProps) {
  const price = formatUsd(PLAN_PRICES_USD.single_scan);
  const patternsText = patternCount > 0 ? `ToxicDetector identified ${patternCount} communication ${patternCount === 1 ? 'pattern' : 'patterns'}. Unlock the complete report to see exactly what triggered each finding.` : 'Unlock the complete report for the full message-by-message breakdown of this conversation.';
  const actions = <CardActions price={price} state={state} error={error} notice={notice} onUnlock={onUnlock} onCheckAgain={onCheckAgain} data-id="element-356" data-mp="1g09x6yi7g6bn" />;
  return <section id="unlock-report" aria-labelledby="unlock-heading" className="scroll-mt-24 rounded-2xl border border-forensic-crimson/45 bg-forensic-card shadow-[0_24px_80px_-40px_rgba(181,20,47,0.55)]" data-id="element-357" data-mp="1prms5yzavclw">
      <div className="grid gap-8 p-5 sm:p-10 lg:grid-cols-[1.15fr_1fr] lg:gap-14" data-id="element-358" data-mp="1yqhwf5isacmk">
        <div data-id="element-359" data-mp="3uw8tugd4z0cp">
          <div className="flex items-center gap-3" data-id="element-360" data-mp="3utmn2iy2kovj">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-forensic-crimson/35 bg-forensic-crimson/10" data-id="element-361" data-mp="3qvf5xmzp1ljt">
              <Lock size={17} className="text-forensic-crimson-soft" aria-hidden="true" data-id="element-362" data-mp="8i8zq71aalnf" />
            </span>
            <div data-id="element-363" data-mp="iekdl1givjjz">
              <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-forensic-subtle" data-id="element-364" data-mp="ie2kuet2jngt">
                Report Status
              </p>
              <p className="text-sm font-medium text-white" data-id="element-365" data-mp="df7rwk1e0iiz">Full report sealed</p>
            </div>
          </div>
          <h2 id="unlock-heading" className="mt-6 text-2xl font-semibold leading-tight tracking-tight text-white sm:text-[28px]" data-id="element-366" data-mp="rd62rfgi1wtq">
            Your complete forensic analysis is ready
          </h2>
          <p className="mt-3 text-base leading-relaxed text-gray-300" data-id="element-367" data-mp="2o7sjetwie2ta">{patternsText}</p>

          <div className="mt-8 hidden lg:block" data-id="element-368" data-mp="13pi1t5qnwktp">{actions}</div>
        </div>

        <div className="lg:border-l lg:border-forensic-line lg:pl-14" data-id="element-369" data-mp="13rkwi32rf24r">
          <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-forensic-subtle" data-id="element-370" data-mp="18o2zoyifysez">
            Included in the full report
          </p>
          <ul className="mt-4 space-y-3" data-id="element-371" data-mp="yyjf9of2yqi0">
            {reportUnlockFeatures.map(feature => <li key={feature} className="flex items-center gap-3 text-sm text-gray-200" data-id="element-372" data-mp="kz9zgpusdyek">
                <Lock size={13} className="shrink-0 text-forensic-subtle" aria-hidden="true" data-id="element-373" data-mp="py3ggs9a87jh" />
                {feature}
              </li>)}
          </ul>
        </div>

        <div className="lg:hidden" data-id="element-374" data-mp="c0evzlj4n2ey">{actions}</div>
      </div>
    </section>;
}
function CardActions({
  price,
  state,
  error,
  notice,
  onUnlock,
  onCheckAgain
}: {
  price: string;
  state: CheckoutState;
  error: string | null;
  notice: PaymentReturnNotice | null;
  onUnlock: () => void;
  onCheckAgain: () => void;
}) {
  return <div data-id="element-375" data-mp="1lnu0ksrnlm2p">
      <StatusNotice state={state} error={error} notice={notice} onCheckAgain={onCheckAgain} data-id="element-376" data-mp="1lnk6ou3hmurq" />
      <UnlockButton label={`Unlock Full Forensic Report — ${price}`} state={state} onClick={onUnlock} fullWidth data-id="element-377" data-mp="1gnxveq77zolk" />
      <p className="mt-4 text-center text-sm text-forensic-muted" data-id="element-378" data-mp="1br7gw7b2d129">
        One-time payment · No subscription required
      </p>
      <p className="mt-1.5 flex items-center justify-center gap-1.5 text-xs text-forensic-subtle" data-id="element-379" data-mp="12o6diedl1o2a">
        <ShieldCheck size={12} aria-hidden="true" data-id="element-380" data-mp="xxzoidf5s1ji" />
        Secure checkout · Your conversation remains private
      </p>
    </div>;
}
function StatusNotice({
  state,
  error,
  notice,
  onCheckAgain
}: {
  state: CheckoutState;
  error: string | null;
  notice: PaymentReturnNotice | null;
  onCheckAgain: () => void;
}) {
  if (state === 'confirming') {
    return <div role="status" aria-live="polite" className="mb-4 flex items-start gap-3 rounded-xl border border-forensic-line bg-forensic-surface px-4 py-3" data-id="element-381" data-mp="j29ze6eof8ak">
        <Loader2 size={16} className="mt-0.5 shrink-0 animate-spin text-forensic-muted" aria-hidden="true" data-id="element-382" data-mp="2qlxpawy6wmuy" />
        <div data-id="element-383" data-mp="2upvm3101llku">
          <p className="text-sm font-medium text-white" data-id="element-384" data-mp="2zmvtbf8moudj">Confirming payment…</p>
          <p className="mt-0.5 text-sm text-forensic-muted" data-id="element-385" data-mp="2zkst62drhkv3">
            Your report unlocks as soon as payment is confirmed. Keep this page open.
          </p>
        </div>
      </div>;
  }
  if (state === 'timed_out') {
    return <div role="status" aria-live="polite" className="mb-4 rounded-xl border border-forensic-line bg-forensic-surface px-4 py-3" data-id="element-386" data-mp="2bwrpb6rj2sm0">
        <p className="text-sm font-medium text-white" data-id="element-387" data-mp="2gub4q9zypbfj">Still locked</p>
        <p className="mt-0.5 text-sm text-forensic-muted" data-id="element-388" data-mp="2lnw1xztqe1do">
          If you were charged, it will unlock shortly.
        </p>
        <button type="button" onClick={onCheckAgain} className="mt-2 inline-flex min-h-[36px] items-center gap-1.5 text-sm font-medium text-white underline-offset-4 transition-colors duration-150 ease-out hover:text-forensic-crimson-soft hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-forensic-crimson-soft" data-id="element-389" data-mp="1o5ma8ttew4cr">
          <RefreshCw size={14} aria-hidden="true" data-id="element-390" data-mp="1uvf7c721biby" />
          Check again
        </button>
      </div>;
  }
  if (state === 'still_locked') {
    return <div role="status" className="mb-4 rounded-xl border border-forensic-line bg-forensic-surface px-4 py-3" data-id="element-391" data-mp="126q1juq1aotv">
        <p className="text-sm font-medium text-white" data-id="element-392" data-mp="y6fzuu99ykkt">Still locked</p>
        <p className="mt-0.5 text-sm text-forensic-muted" data-id="element-393" data-mp="1c5fmjwd88pec">
          {notice ? NOTICE_COPY[notice] : 'Checkout didn’t complete, so this report is still locked. You can try again below.'}
        </p>
      </div>;
  }
  if (state === 'error' && error) {
    return <p role="alert" className="mb-4 flex items-start gap-2 text-sm text-forensic-crimson-soft" data-id="element-394" data-mp="2ilhwofekea6v">
        <AlertTriangle size={15} className="mt-0.5 shrink-0" aria-hidden="true" data-id="element-395" data-mp="4dqfqsgz9715" />
        {error}
      </p>;
  }
  return null;
}